import {
  keepPreviousData,
  MutationFunction,
  QueryFunctionContext,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query'
import { getApiClient } from '../api-client'

export interface UserRights {
  canAddUser: boolean
  canEditUser: boolean
  canSetReportProperties: boolean
  canManagePromotions: boolean
}

export type UserRight = keyof UserRights

/** The editable user profile served by `/MyProfile` (distinct from the auth `IfUser`). */
export interface User {
  id: string
  firstName: string
  lastName: string
  fullName: string
  email: string
  language: string
  isAdmin: boolean
  jobTitle: string
  accountEnabled: boolean
  merchantId: string
  roles: string[]
  rights: UserRights
  isInternal: boolean
  phone: string
  exp: number /* expire date in ms. to get the date, do new Date(user.exp * 1000) */
}

export interface JwtToken {
  jwt: string
}

export interface MerchantUsersStatsRequestDto {
  merchantIds: string[]
  createdOnDateLimit: Date
}

/**
 * A row from `/MyProfile/Users`: a `User` enriched with the display + per-period
 * application-stat fields the merchant-users grid renders. This is the real shape
 * the endpoint returns, so consumers read these fields without casting.
 */
export interface MerchantUserRow extends User {
  userFullName: string
  merchant: string
  lastConnection: string
  allApplications: number
  active: number
  completed: number
  draft: number
  expired: number
  cancelled: number
}

const SCOPE = 'user-profile'
const DETAIL = 'detail'
const MERCHANT_USERS = 'merchant-users'

const keysFactory = {
  detail: () => [{ scope: SCOPE, entity: DETAIL }] as const,
  merchantUsers: (merchantIds: string[], createdOnDateLimit: Date) =>
    [{ scope: SCOPE, entity: MERCHANT_USERS, merchantIds, createdOnDateLimit }] as const,
}

const getUserProfile = async (_ctx: QueryFunctionContext<ReturnType<(typeof keysFactory)['detail']>>) => {
  const apiClient = getApiClient()
  const response = await apiClient.get('/MyProfile')

  return response.data as User
}

export function useGetUserProfile(userId: string | undefined, isFederatedUser: boolean): [User | null, boolean] {
  const { isFetching, data } = useQuery({
    queryKey: keysFactory.detail(),
    queryFn: getUserProfile,
    enabled: !!userId && !isFederatedUser, // #62287 — skip fetching user profile for federated users since their profile is managed in Entra ID Iceberg, not B2C
  })

  return [data ?? null, isFetching]
}

const getMerchantUsersStats = async ({
  queryKey: [{ merchantIds, createdOnDateLimit }],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['merchantUsers']>>) => {
  const apiClient = getApiClient()
  const dto: MerchantUsersStatsRequestDto = { merchantIds, createdOnDateLimit }
  const response = await apiClient.post('/MyProfile/Users', dto)
  return response.data as MerchantUserRow[]
}

// Returns the raw query so `PageContainer`'s resource gate can own loading/error.
// `keepPreviousData` keeps the previous rows on-screen while a period change
// refetches, so the page doesn't blank (status stays `success`) on every toggle.
export function useGetMerchantUsersQuery(dto: MerchantUsersStatsRequestDto): UseQueryResult<MerchantUserRow[]> {
  return useQuery({
    queryKey: keysFactory.merchantUsers(dto.merchantIds, dto.createdOnDateLimit),
    queryFn: getMerchantUsersStats,
    placeholderData: keepPreviousData,
  })
}

const updateUserProfile: MutationFunction<User, User> = async (dto: User) => {
  const apiClient = getApiClient()
  const response = await apiClient.put('/MyProfile', dto)
  return response.data as User
}

export function useUpdateUserProfile(): [MutationFunction<User, User>, boolean, boolean, () => void] {
  const queryClient = useQueryClient()
  const { isPending, isError, mutateAsync, reset } = useMutation({
    mutationFn: updateUserProfile,
    onSuccess: (data) => {
      queryClient.setQueryData(keysFactory.detail(), data)
      return data
    },
  })
  return [mutateAsync, isPending, isError, reset]
}

/** Enable/disable a user's account. The caller states the desired status; the
 *  hook assembles the `ManageProfile` body from the user + that flag. */
export interface ManageUserAccountInput {
  user: User
  accountEnabled: boolean
}

export const manageUserProfile: MutationFunction<User, ManageUserAccountInput> = async ({ user, accountEnabled }) => {
  const apiClient = getApiClient()
  const response = await apiClient.put('/MyProfile/ManageProfile', { ...user, accountEnabled })
  return response.data as User
}

export function useManageUserProfile(): [MutationFunction<User, ManageUserAccountInput>, boolean, boolean, () => void] {
  const queryClient = useQueryClient()
  const { isPending, isError, mutateAsync, reset } = useMutation({
    mutationFn: manageUserProfile,
    onSuccess: (data) => {
      // Patch the row from the response instead of refetching: Graph list queries lag
      // behind the PATCH, so an immediate refetch returns the previous status.
      // Partial key match covers every merchantIds/period variant.
      queryClient.setQueriesData<MerchantUserRow[]>({ queryKey: [{ scope: SCOPE, entity: MERCHANT_USERS }] }, (rows) =>
        rows?.map((row) => (row.id === data.id ? { ...row, accountEnabled: data.accountEnabled } : row)),
      )
      return data
    },
  })

  return [mutateAsync, isPending, isError, reset]
}
